import { type PropsWithChildren, type RefObject, useId } from "react";

import { ButtonContext, Provider } from "react-aria-components";

import { customTv } from "#utils";

import type { VariantProps } from "tailwind-variants";

const buttonGroupStyles = customTv({
  base: "flex gap-xs",
  variants: {
    orientation: {
      horizontal: "flex-row",
      vertical: "flex-col",
    },
    align: {
      start: "",
      center: "",
      end: "",
    },
  },
  compoundVariants: [
    // Horizontal alignment
    {
      orientation: "horizontal",
      align: "start",
      class: "justify-start",
    },
    {
      orientation: "horizontal",
      align: "center",
      class: "justify-center",
    },
    {
      orientation: "horizontal",
      align: "end",
      class: "justify-end",
    },
    // Vertical alignment
    {
      orientation: "vertical",
      align: "start",
      class: "items-start",
    },
    {
      orientation: "vertical",
      align: "center",
      class: "items-center",
    },
    {
      orientation: "vertical",
      align: "end",
      class: "items-end",
    },
  ],
  defaultVariants: {
    orientation: "horizontal",
    align: "start",
  },
});

export type ButtonGroupOrientation = "horizontal" | "vertical";
export type ButtonGroupAlignment = "start" | "center" | "end";

export interface ButtonGroupProps
  extends VariantProps<typeof buttonGroupStyles>, PropsWithChildren {
  /** Whether the buttons in the ButtonGroup are all disabled */
  isDisabled?: boolean;
  /** The axis the ButtonGroup should align with */
  orientation?: ButtonGroupOrientation;
  /** The alignment of the buttons within the ButtonGroup */
  align?: ButtonGroupAlignment;
  /** Additional CSS class names */
  className?: string;
  /** Forward ref to the container element */
  ref?: RefObject<HTMLDivElement>;
}

export function ButtonGroup({
  children,
  isDisabled = false,
  orientation = "horizontal",
  align = "start",
  className,
  ref,
}: ButtonGroupProps) {
  const id = useId();

  return (
    <Provider values={[[ButtonContext, { isDisabled }]]}>
      <div
        aria-label="Actions"
        className={buttonGroupStyles({ orientation, align, class: className })}
        id={id}
        ref={ref}
        role="group"
      >
        {children}
      </div>
    </Provider>
  );
}
